import { useCallback, useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import type { ParseSuggestion } from "../types";
import { REVIEW_REASON_LABELS, REVIEW_REASON_NOTES } from "../types";
import { ErrorNote } from "./ErrorNote";

type State = "pending" | "ready" | "applied" | "failed";

/**
 * What the reader found in a report, for a doctor to work through (ADR-014).
 *
 * The rule this screen exists to hold: a suggestion is not a record. Nothing
 * here reaches the chart until a doctor accepts it (CLN-F-006), and they can
 * correct the value first. What they accept is what the record holds, never
 * what the reader guessed.
 *
 * There is an accept-all, and the reasoning against it was half right.
 * Accepting twenty-three values with one press is accepting whatever the
 * reader read. But pressing accept twenty-three times is not checking
 * twenty-three times either: the check is reading the numbers against the
 * report before any press. One press after that is honest; twenty-three is a
 * tax on having uploaded a long report.
 *
 * What was wrong with it was the four readings the reader had told us it
 * could not read. They sat in the same table as the nineteen it read cleanly,
 * marked with a word, and one press took all twenty-three. So this screen
 * puts them first and on their own, each with the line it came from, and the
 * bulk button is dead until every one of them has an answer. The server
 * refuses it too, with the same count, because a disabled button is a
 * courtesy and not a rule.
 *
 * Each of those has two answers, not one. "Put it on the chart" was always
 * there; "leave it out" was not, and without it a doctor who judged a reading
 * wrong had no way to say so. It stayed pending, the review could never be
 * finished, and the next person met the same question with no sign anybody
 * had answered it.
 *
 * Releasing is offered beside the bulk accept for a reason that has nothing
 * to do with convenience. A record is held from the patient unless the clinic
 * opted out (CLN-F-020), so a doctor who accepts a report and stops has put
 * values on the chart that the patient cannot see, and from the patient's
 * side that is indistinguishable from the upload having failed.
 */

/** The range the laboratory itself printed, if it printed one. */
function labRange(suggestion: ParseSuggestion) {
  const low = suggestion.reference_low;
  const high = suggestion.reference_high;
  if (low && high) return `${low} – ${high}`;
  if (high) return `under ${high}`;
  if (low) return `over ${low}`;
  return "";
}

/**
 * The reading as one string, for a row that has been answered.
 *
 * What was filed, not what was read. A doctor who typed in the unit the
 * report left off should not be shown their own reading back without it.
 */
function reading(suggestion: ParseSuggestion) {
  if (suggestion.status === "skipped") return "—";
  return [
    suggestion.accepted_value || suggestion.value,
    suggestion.accepted_unit || suggestion.unit,
  ]
    .filter(Boolean)
    .join(" ");
}

export function ParseReview({
  documentId,
  onChanged,
}: {
  documentId: string;
  /**
   * Something reached the chart. The table above this one is showing the
   * records as they were when the page loaded, so a doctor who accepted six
   * readings watched the chart not change and had no way to tell whether
   * anything had happened.
   */
  onChanged?: () => void;
}) {
  const [state, setState] = useState<State>("pending");
  const [suggestions, setSuggestions] = useState<ParseSuggestion[]>([]);
  /**
   * The gate, as the server counts it.
   *
   * This screen could count the flagged rows itself, and would then be able
   * to disagree with the server about whether the bulk button may be pressed.
   * That is the one thing it must not do, so the number comes across with the
   * readings.
   */
  const [pendingReview, setPendingReview] = useState(0);
  const [failureReason, setFailureReason] = useState("");
  const [showClean, setShowClean] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [alsoRelease, setAlsoRelease] = useState(true);
  const [filed, setFiled] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<number, string>>({});
  /**
   * The unit, separately from the value.
   *
   * It used to be printed and not editable, which was fine until a reader
   * misread one. A cholesterol of 6.14 means one thing in mmol/L and
   * something about thirty-nine times different in mg/dL, and a doctor who
   * could see the wrong unit had no way to correct it.
   */
  const [units, setUnits] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed } = await api.GET("/portal/documents/{id}/parse", {
      params: { path: { id: documentId }, header: clinicHeader() },
    });
    setLoading(false);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setState((data?.state ?? "pending") as State);
    setSuggestions((data?.suggestions ?? []) as ParseSuggestion[]);
    setPendingReview(data?.pending_review ?? 0);
    setFailureReason(data?.failure_reason ?? "");
  }, [documentId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** What the doctor typed over the reader's value, if anything. */
  function corrections(suggestion: ParseSuggestion) {
    const index = suggestion.suggestion_index ?? 0;
    const overrides: Record<string, string> = {};
    const typedValue = edits[index];
    const typedUnit = units[index];
    if (typedValue !== undefined && typedValue !== suggestion.value) {
      overrides.value = typedValue;
    }
    if (typedUnit !== undefined && typedUnit !== (suggestion.unit ?? "")) {
      overrides.unit = typedUnit;
    }
    return overrides;
  }

  async function accept(suggestion: ParseSuggestion) {
    const index = suggestion.suggestion_index ?? 0;
    setBusy(index);
    setError(null);
    const overrides = corrections(suggestion);

    const { error: failed } = await api.POST("/portal/documents/{id}/parse/confirm", {
      params: { path: { id: documentId }, header: clinicHeader() },
      body: {
        suggestion_index: index,
        ...(Object.keys(overrides).length ? { overrides } : {}),
      },
    });
    setBusy(null);

    if (failed) {
      setError(readError(failed).message);
    } else {
      onChanged?.();
    }
    // Either way. A refusal here is almost always somebody else having
    // answered this reading already, and what the screen is showing is then
    // out of date in a way that reloading fixes and an error message does
    // not.
    void load();
  }

  /** Leave one reading out, on purpose. It writes nothing clinical. */
  async function skip(suggestion: ParseSuggestion) {
    const index = suggestion.suggestion_index ?? 0;
    setBusy(index);
    setError(null);

    const { error: failed } = await api.POST("/portal/documents/{id}/parse/skip", {
      params: { path: { id: documentId }, header: clinicHeader() },
      body: { suggestion_index: index },
    });
    setBusy(null);

    if (failed) {
      setError(readError(failed).message);
    }
    // Nothing clinical was written, so the chart has not changed. The review
    // has, and that is all this reloads.
    void load();
  }

  /**
   * Accept everything the reader read cleanly, and optionally release it.
   *
   * Corrected rows are left out: an edit is a correction, and a correction is
   * accepted on its own row where the doctor can see what they changed.
   */
  async function acceptClean(indexes: number[]) {
    setBulk(true);
    setError(null);
    setFiled(null);
    try {
      const { data, error: failed } = await api.POST(
        "/portal/documents/{id}/parse/confirm-all",
        {
          params: { path: { id: documentId }, header: clinicHeader() },
          body: { indexes, release: alsoRelease },
        },
      );

      if (failed) {
        setError(readError(failed).message);
        void load();
        return;
      }

      onChanged?.();
      const n = data?.confirmed ?? indexes.length;
      setFiled(
        alsoRelease
          ? `${n} on the chart, and the patient can see them.`
          : `${n} on the chart. The patient cannot see them until you release them.`,
      );
      void load();
    } finally {
      setBulk(false);
    }
  }

  async function finish() {
    const { error: failed } = await api.POST("/portal/documents/{id}/parse/reviewed", {
      params: { path: { id: documentId }, header: clinicHeader() },
    });
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    void load();
  }

  if (loading && suggestions.length === 0) {
    return <p className="empty">Loading the readings…</p>;
  }

  if (state === "pending") {
    return <p className="empty">This report has not been read yet. Check back shortly.</p>;
  }

  if (state === "failed") {
    return (
      <p className="empty">
        The reader could not read this report{failureReason ? `: ${failureReason}` : "."} The
        file is still here, and you can enter the values yourself.
      </p>
    );
  }

  if (suggestions.length === 0) {
    return <p className="empty">The reader found no measurements in this report.</p>;
  }

  const flagged = suggestions.filter((row) => row.needs_review);
  const open = flagged.filter((row) => row.status === "pending");
  const answered = flagged.filter((row) => row.status !== "pending");
  const clean = suggestions.filter((row) => !row.needs_review);
  const cleanOpen = clean.filter((row) => row.status === "pending");
  const leftOut = suggestions.filter((row) => row.status === "skipped").length;

  // Corrected rows go one at a time: accept-all has nothing to correct
  // against.
  const untouched = cleanOpen
    .map((row) => row.suggestion_index ?? 0)
    .filter((index) => edits[index] === undefined && units[index] === undefined);

  const gateUp = pendingReview > 0;

  return (
    <div className="parse">
      <ErrorNote message={error} />

      {gateUp && (
        <div className="parse-gate" role="status">
          <div className="parse-gate-says">
            <p className="parse-gate-count">
              {open.length === 1
                ? "1 reading needs you before the rest can go in"
                : `${open.length} readings need you before the rest can go in`}
            </p>
            <p className="parse-gate-why">
              The reader was unsure of these. Each needs a yes or a skip.
            </p>
          </div>
          <div className="parse-gate-progress">
            <span>
              {answered.length} of {flagged.length} done
            </span>
            <div
              className="parse-gate-bar"
              role="progressbar"
              aria-valuenow={answered.length}
              aria-valuemin={0}
              aria-valuemax={flagged.length}
            >
              <div
                className="parse-gate-bar-fill"
                style={{ width: `${(answered.length / flagged.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {!gateUp && flagged.length > 0 && (
        <div className="parse-gate cleared" role="status">
          <div className="parse-gate-says">
            <p className="parse-gate-count">
              {flagged.length === 1 ? "The one in doubt is answered" : `All ${flagged.length} answered`}
            </p>
            <p className="parse-gate-why">
              {flagged.length - answered.filter((row) => row.status === "skipped").length}{" "}
              on the chart, {answered.filter((row) => row.status === "skipped").length} left
              out. The rest can go in together.
            </p>
          </div>
        </div>
      )}

      {/* One card each, with the line the reader took the value from. Without
          it a doctor is asked whether 4.07 is right with nothing to check it
          against. */}
      <ol className="parse-doubtful">
        {open.map((suggestion) => {
          const index = suggestion.suggestion_index ?? 0;
          const reason = suggestion.review_reason ?? "";
          const range = labRange(suggestion);
          return (
            <li key={index}>
              <div className="parse-doubtful-head">
                <h3>{suggestion.display}</h3>
                <span className="tag warn">
                  {REVIEW_REASON_LABELS[reason] ?? "Needs checking"}
                </span>
              </div>

              <p className="parse-doubtful-why">{REVIEW_REASON_NOTES[reason] ?? ""}</p>

              {suggestion.source_text && (
                <p className="parse-doubtful-line">{suggestion.source_text}</p>
              )}

              <div className="parse-doubtful-answer">
                <div className="field">
                  <label htmlFor={`value-${index}`}>Value</label>
                  <input
                    id={`value-${index}`}
                    className="value"
                    aria-label={`${suggestion.display} value`}
                    value={edits[index] ?? suggestion.value ?? ""}
                    onChange={(event) =>
                      setEdits({ ...edits, [index]: event.target.value })
                    }
                  />
                </div>
                <div className="field">
                  <label htmlFor={`unit-${index}`}>Unit</label>
                  <input
                    id={`unit-${index}`}
                    className="unit"
                    aria-label={`${suggestion.display} unit`}
                    placeholder="none read"
                    value={units[index] ?? suggestion.unit ?? ""}
                    onChange={(event) =>
                      setUnits({ ...units, [index]: event.target.value })
                    }
                  />
                </div>
                {range && <p className="parse-doubtful-range">Lab range {range}</p>}
                <div className="parse-doubtful-buttons">
                  <button
                    type="button"
                    disabled={busy === index}
                    onClick={() => void accept(suggestion)}
                  >
                    {busy === index ? "Adding…" : "Put it on the chart"}
                  </button>
                  <button
                    type="button"
                    className="quiet"
                    disabled={busy === index}
                    onClick={() => void skip(suggestion)}
                  >
                    Leave it out
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {/* Answered rows stay on screen rather than disappearing. A doctor who
          left something out needs to see that they left it out, and to be
          able to change their mind before the bulk press. */}
      {answered.length > 0 && (
        <ul className="parse-answered">
          {answered.map((suggestion) => {
            const index = suggestion.suggestion_index ?? 0;
            const out = suggestion.status === "skipped";
            return (
              <li key={index}>
                <span className={out ? "dot out" : "dot on"} aria-hidden="true" />
                <span className="parse-answered-name">{suggestion.display}</span>
                <span className="parse-answered-reading">{reading(suggestion)}</span>
                <span className="parse-answered-outcome">
                  {out ? "Left out" : "On the chart"}
                </span>
                {/* Only a reading left out can be changed here. One on the
                    chart is a record, and a record is corrected on the chart
                    so that what it said before is still there. */}
                {out && (
                  <button
                    type="button"
                    className="link"
                    disabled={busy === index}
                    onClick={() => void accept(suggestion)}
                  >
                    {busy === index ? "Adding…" : "Put it on after all"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="parse-rest">
        <div className="parse-rest-head">
          <div>
            {/* A badly scanned report can flag everything, and then there is
                no "rest" to speak of. Saying so once is better than a
                heading that counts to nought. */}
            {clean.length === 0 ? (
              <h3>Every reading in this report needs an answer.</h3>
            ) : (
              <>
                <h3>
                  {cleanOpen.length === 0
                    ? `${clean.length} read cleanly`
                    : `${cleanOpen.length} more, read cleanly`}
                </h3>
                <p className="muted">
                  {clean
                    .slice(0, 4)
                    .map((row) => row.display)
                    .join(", ") +
                    (clean.length > 4 ? ` and ${clean.length - 4} others.` : ".")}
                </p>
              </>
            )}
          </div>
          {clean.length > 0 && (
            <button type="button" className="quiet" onClick={() => setShowClean(!showClean)}>
              {showClean ? "Hide them" : "Show them"}
            </button>
          )}
        </div>

        {showClean && (
          <table>
            <thead>
              <tr>
                <th>Measurement</th>
                <th>Value</th>
                <th>Unit</th>
                <th>Lab range</th>
                <th>Read from</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {clean.map((suggestion) => {
                const index = suggestion.suggestion_index ?? 0;
                const done = suggestion.status !== "pending";
                return (
                  <tr key={index}>
                    <td>{suggestion.display}</td>
                    <td>
                      <input
                        className="value"
                        aria-label={`${suggestion.display} value`}
                        value={edits[index] ?? suggestion.value ?? ""}
                        disabled={done}
                        onChange={(event) =>
                          setEdits({ ...edits, [index]: event.target.value })
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="unit"
                        aria-label={`${suggestion.display} unit`}
                        placeholder="none read"
                        value={units[index] ?? suggestion.unit ?? ""}
                        disabled={done}
                        onChange={(event) =>
                          setUnits({ ...units, [index]: event.target.value })
                        }
                      />
                    </td>
                    <td className="muted">{labRange(suggestion) || "—"}</td>
                    <td className="muted source">{suggestion.source_text}</td>
                    <td>
                      {suggestion.status === "confirmed" ? (
                        <span className="tag open">On the chart</span>
                      ) : suggestion.status === "skipped" ? (
                        <span className="tag">Left out</span>
                      ) : (
                        <button
                          type="button"
                          className="link"
                          disabled={busy === index}
                          onClick={() => void accept(suggestion)}
                        >
                          {busy === index ? "Adding…" : "Accept"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {cleanOpen.length > 0 && (
          <div className={gateUp ? "parse-bulk locked" : "parse-bulk"}>
            {untouched.length > 0 && (
              <button
                type="button"
                disabled={gateUp || bulk}
                onClick={() => void acceptClean(untouched)}
              >
                {bulk ? "Filing…" : `Accept the other ${untouched.length}`}
              </button>
            )}
            {!gateUp && untouched.length > 0 && (
              <label className="parse-release">
                <input
                  type="checkbox"
                  checked={alsoRelease}
                  onChange={(event) => setAlsoRelease(event.target.checked)}
                  disabled={bulk}
                />
                <span>and let the patient see them</span>
              </label>
            )}
            <p className="muted">
              {gateUp
                ? `Available once the ${open.length} above are answered.`
                : untouched.length === 0
                  ? "Every one of these has been corrected, so each is accepted on its own row."
                  : `${untouched.length} readings go to the chart.` +
                    (leftOut > 0
                      ? ` The ${leftOut} you left out ${leftOut === 1 ? "does" : "do"} not.`
                      : "")}
            </p>
          </div>
        )}
      </div>

      {filed && (
        <p className="parse-filed" role="status">
          {filed}
        </p>
      )}

      {state !== "applied" && !gateUp && (
        <button type="button" className="link" onClick={() => void finish()}>
          I have been through these
        </button>
      )}
      {state === "applied" && <p className="muted">Reviewed.</p>}
    </div>
  );
}
